import {
  AdditionalInfoCard,
  ContactsCard,
  DateAndTimeCard,
  ExtraServicesCard,
  FreelanceOfferCard,
  KeysCard,
  NotesCard,
  OrderReferenceCard,
  OrderServiceListCard,
  PhotoCountCard,
  PropertyAddressCard
} from "../Cards";
import { Button, ButtonsGroup, Heading, PageHeader, Typo } from "#/atoms";
import { FONT_SIZE, NATURAL } from "@/theme";
import { forwardRef, memo, useImperativeHandle, useRef } from "react";

import { CardHandle } from "../Cards/Cards.interface";
import { OrderBookingProps } from "./Booking.interface";

const Booking = forwardRef<CardHandle, OrderBookingProps> (
({
  addressString,
  allowFreelance,
  appointmentDate,
  appointmentDuration,
  appointmentTime,
  bedrooms,
  contacts,
  contactVendorDirectly,
  description,
  doorNumber,
  flat,
  floorArea,
  imageMotionRequested,
  isAdmin,
  keyNotes,
  keys,
  keysTime,
  keysTimeOption,
  keysWith,
  keysWithDetail,
  notes,
  notifyViaSMS,
  orderOfferPrice,
  orderReference,
  onAddCustomerContact,
  onAddNote,
  onAskToChangeWorker,
  onAskToEditContact,
  onAskToRemoveContact,
  onAskToAddNewContact,
  onCancelNote,
  onChangePackage,
  onSaveNote,
  owner,
  parking,
  photoCount,
  poNumber,
  preferredWorker,
  selectedOrderTemplate,
  sendNotifications,
  services,
  videoMotionRequested,
  virtualStagingRequested,
  virtualTourPartial
}, ref) => { 
  const accordionState = 'open';

    const additionalInfoRef = useRef<CardHandle>(null);
    const contactsRef = useRef<CardHandle>(null);
    const dateAndTimeRef = useRef<CardHandle>(null);
    const keysRef = useRef<CardHandle>(null);
    const propertyAddressRef = useRef<CardHandle>(null);

      useImperativeHandle(ref, () => ({
        validate: () => {
          const addResult = additionalInfoRef.current?.validate();
          const contactsResult = contactsRef.current?.validate();
          const dateAndTimeResult = dateAndTimeRef.current?.validate();
          const propertyAddressResult = propertyAddressRef.current?.validate();

          const isValid = (!additionalInfoRef.current || addResult?.isValid) 
                          && contactsResult?.isValid 
                          && (!dateAndTimeRef.current || dateAndTimeResult?.isValid)
                          && (!propertyAddressRef.current || propertyAddressResult?.isValid);

          return isValid ? { isValid: true} 
          : { isValid: false, validationErrors: [
                  ...dateAndTimeResult?.validationErrors ?? [],
                  ...propertyAddressResult?.validationErrors ?? [],
                  ...addResult?.validationErrors ?? [],
                  ...contactsResult?.validationErrors ?? []] };
        }
      }));

  return (
    <>
      <PageHeader>
        <Heading scale="h1" fontColor={NATURAL.K}>{description}</Heading>
        <Typo fontColor={NATURAL.G} fontSize={FONT_SIZE.SM}>Please provide us information about your order.</Typo>
        <ButtonsGroup>
          <Button actionType='outline' onClick={onChangePackage}>Change Package</Button>
          
        </ButtonsGroup>
      </PageHeader>

      { selectedOrderTemplate?.isVirtual && <OrderReferenceCard
        accordionState={accordionState}
        orderReference={orderReference} />}

      { selectedOrderTemplate?.id === 10468 && <PhotoCountCard
        accordionState={accordionState}
        photoCount={photoCount} />}

      { !selectedOrderTemplate?.isVirtual && <DateAndTimeCard
        accordionState={accordionState}
        appointmentDate={appointmentDate}
        appointmentTime={appointmentTime}
        appointmentDuration={appointmentDuration}
        contactVendorDirectly={contactVendorDirectly}
        notifyViaSMS={notifyViaSMS}
        ref={dateAndTimeRef}
      /> }
      
      { !selectedOrderTemplate?.isVirtual && <PropertyAddressCard
        accordionState={accordionState}
        addressString={addressString}
        flat={flat}
        doorNumber={doorNumber}
        ref={propertyAddressRef}
      /> }

      { !selectedOrderTemplate?.isVirtual && <KeysCard
        accordionState={accordionState}
        keys={keys}
        keysTime={keysTime}
        keysTimeOption={keysTimeOption}
        keysWith={keysWith}
        keysWithDetail={keysWithDetail}
        keyNotes={keyNotes}
        ref={keysRef}
      /> }

      { !selectedOrderTemplate?.isVirtual && <AdditionalInfoCard
        accordionState={accordionState}
        poNumber={poNumber}
        bedrooms={bedrooms}
        floorArea={floorArea}
        parking={parking}
        virtualTourPartial={virtualTourPartial}
        ref={additionalInfoRef}
      /> }

      <ContactsCard
        accordionState={accordionState}
        contacts={contacts}
        isAdmin={isAdmin}
        onAddCustomerContact={onAddCustomerContact}
        onAskToEditContact={onAskToEditContact}
        onAskToRemoveContact={onAskToRemoveContact}
        onNewContact={onAskToAddNewContact}
        ref={contactsRef}
        sendNotifications={sendNotifications}
      />
      <NotesCard
        accordionState={accordionState}
        notes={notes}
        onAddNote={onAddNote}
        onCancelNote={onCancelNote}
        onSaveNote={onSaveNote}
        owner={owner}
      />

      <ExtraServicesCard 
        accordionState={accordionState}
        imageMotionRequested={imageMotionRequested}
        videoMotionRequested={videoMotionRequested}
        virtualStagingRequested={virtualStagingRequested}
      />

      {isAdmin && (
      <FreelanceOfferCard
          onChangeRole={() => onAskToChangeWorker()}
          accordionState={accordionState}
          allowFreelance={allowFreelance}
          orderOfferPrice={orderOfferPrice}
          preferredWorker={preferredWorker}
        />
      )}
    
      <OrderServiceListCard
          accordionState={accordionState}
          services={services}
        />
    </>
  );
}
);

Booking.displayName = 'Booking';

export default memo(Booking);
